/* ─────────────────────────────────────────────────────────────────
   PYTHON DATA SCIENCE — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const PYTHON_DS_MODULES = [
  {
    module: 1,
    title: "Python for Data Analysis",
    emoji: "📊",
    topics: [
      {
        id: 1,
        topic: "NumPy & Pandas Fundamentals",
        description: "Learn the core data structures of data science in Python — NumPy arrays and Pandas DataFrames.",
        motivation: "Great start! NumPy and Pandas are the foundation every data scientist builds on daily. 📊",
        resource: {
          name: "Pandas Documentation — Getting Started",
          url: "https://pandas.pydata.org/docs/getting_started/index.html",
        },
        task: "Load and explore a real-world dataset (e.g. Titanic or a dataset of your choice) using Pandas.",
        requirements: [
          "Load the dataset into a DataFrame and display its shape and columns",
          "Use NumPy to compute basic stats (mean, median, std) on a numeric column",
          "Check for and count missing values per column",
          "Filter and sort the DataFrame in at least 2 different ways",
          "Submit a Jupyter notebook (.ipynb) in your GitHub repository",
        ],
      },
      {
        id: 2,
        topic: "Data Cleaning & Preprocessing",
        description: "Learn to handle missing values, duplicates, and inconsistent data before analysis.",
        motivation: "Clean data, reliable insights — this is the unglamorous work that makes every good analysis possible. 🧹",
        resource: {
          name: "Pandas Documentation — Working with Missing Data",
          url: "https://pandas.pydata.org/docs/user_guide/missing_data.html",
        },
        task: "Clean your dataset and prepare it for analysis.",
        requirements: [
          "Handle missing values appropriately (drop, fill, or impute — justify your choice)",
          "Remove duplicate rows if present",
          "Convert columns to correct data types (dates, categories, numerics)",
          "Create at least one new derived column (feature engineering)",
          "Add markdown cells explaining each cleaning decision",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "Data Visualization",
    emoji: "📈",
    topics: [
      {
        id: 3,
        topic: "Visualization with Matplotlib & Seaborn",
        description: "Learn to create clear, informative charts to communicate patterns in data.",
        motivation: "Your charts tell a story that raw numbers can't. Visualization is where data becomes wisdom! 🌟",
        resource: {
          name: "Seaborn Official Tutorial",
          url: "https://seaborn.pydata.org/tutorial.html",
        },
        task: "Create a visualization dashboard in a Jupyter notebook with at least 5 chart types.",
        requirements: [
          "Bar chart comparing a categorical variable",
          "Heatmap of the correlation matrix of numerical features",
          "Histogram showing the distribution of a key numeric column",
          "Box plot comparing a numeric variable across categories",
          "One chart type of your choice relevant to your dataset",
          "All charts must have titles, axis labels, and legends",
        ],
      },
      {
        id: 4,
        topic: "Exploratory Data Analysis (EDA)",
        description: "Learn to systematically explore a dataset to uncover patterns, trends, and anomalies before modeling.",
        motivation: "You're asking the right questions of your data now — that's the real skill behind every good data scientist. 🔍",
        resource: {
          name: "Pandas Documentation — EDA Basics",
          url: "https://pandas.pydata.org/docs/user_guide/basics.html",
        },
        task: "Perform a full exploratory data analysis on your chosen dataset.",
        requirements: [
          "Identify and describe at least 3 key trends or patterns",
          "Investigate at least one outlier or anomaly and explain it",
          "Compare relationships between 2–3 pairs of variables",
          "Summarize findings in markdown cells with supporting charts",
          "Keep the notebook well organized with clear section headers",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "Statistics & Intro to Machine Learning",
    emoji: "🧮",
    topics: [
      {
        id: 5,
        topic: "Descriptive Statistics & Hypothesis Testing",
        description: "Learn core statistical concepts and how to test whether a pattern in data is meaningful.",
        motivation: "Statistics turns 'it looks like a pattern' into 'it IS a pattern' — you just leveled up your rigor. 🧮",
        resource: {
          name: "Khan Academy — Statistics and Probability",
          url: "https://www.khanacademy.org/math/statistics-probability",
        },
        task: "Apply statistical analysis to validate a hypothesis about your dataset.",
        requirements: [
          "State a clear hypothesis about your data (e.g. 'Group A has a higher average than Group B')",
          "Calculate relevant descriptive statistics for each group being compared",
          "Run an appropriate statistical test (t-test, chi-square, etc.)",
          "Interpret the p-value and state your conclusion in plain English",
          "Visualize the comparison with an appropriate chart",
        ],
      },
      {
        id: 6,
        topic: "Intro to Machine Learning with Scikit-learn",
        description: "Learn to train and evaluate a simple machine learning model using scikit-learn.",
        motivation: "You just trained your first model — welcome to machine learning! This is a huge milestone. 🤖",
        resource: {
          name: "Scikit-learn — Getting Started",
          url: "https://scikit-learn.org/stable/getting_started.html",
        },
        task: "Train a simple classification or regression model on your dataset.",
        requirements: [
          "Split data into training and test sets",
          "Train a baseline model (e.g. Logistic Regression or Linear Regression)",
          "Evaluate the model using an appropriate metric (accuracy, RMSE, etc.)",
          "Compare against a second, different model or parameter setting",
          "Explain which model performed better and why, in markdown",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "End-to-End Data Analysis Project",
        description: "Apply everything learned throughout the MicroIntern to deliver a complete, presentable data analysis.",
        motivation: "Incredible! You took raw data all the way to a trained model and a clear story. That's certificate-worthy. 🎉",
        resource: {
          name: "Pandas & Scikit-learn Documentation",
          url: "https://pandas.pydata.org/docs/",
        },
        task: "Choose a dataset (or use one from your earlier tasks) and deliver a complete end-to-end analysis.",
        requirements: [
          "Data cleaning and preprocessing section",
          "At least 5 well-labeled visualizations exploring the data",
          "One statistical test with a clearly stated conclusion",
          "One trained and evaluated machine learning model",
          "A written executive summary (5–8 sentences) of key findings at the top of the notebook",
          "Submit a single, well-organized Jupyter notebook (.ipynb) in your GitHub repository",
          "Include a README.md describing the dataset and how to run the notebook",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const PYTHON_DS_TASKS = PYTHON_DS_MODULES.flatMap((m) => m.topics);
